import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { 
  Loader2, 
  Download, 
  Trash2, 
  Search,
  BarChart3,
  MessageSquare,
  Calendar,
  Settings,
  Copy,
  ExternalLink,
  Save,
  Plus,
  GripVertical,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SITE_URL = 'https://aravellgolfclub.com';

const AdminSurvey = () => {
  const { authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('responses');
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [report, setReport] = useState(null);
  const [totalResponses, setTotalResponses] = useState(0);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [expandedResponse, setExpandedResponse] = useState(null);
  const [savingCategories, setSavingCategories] = useState(false);

  useEffect(() => {
    if (activeTab === 'responses') {
      fetchResponses();
    } else if (activeTab === 'report') {
      fetchReport();
    } else if (activeTab === 'categories') {
      fetchCategories();
    }
  }, [activeTab, filters]);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/survey/admin/responses?limit=100`;
      if (filters.startDate) url += `&start_date=${filters.startDate}`;
      if (filters.endDate) url += `&end_date=${filters.endDate}`;
      
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setResponses(data.responses);
        setTotalResponses(data.total);
      } else {
        console.error('Error response:', response.status);
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/survey/admin/report`;
      const params = [];
      if (filters.startDate) params.push(`start_date=${filters.startDate}`);
      if (filters.endDate) params.push(`end_date=${filters.endDate}`);
      if (params.length) url += `?${params.join('&')}`;
      
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/survey/admin/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCategories = async () => {
    setSavingCategories(true);
    try {
      const response = await authFetch(`${API_URL}/api/survey/admin/categories`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categories })
      });
      if (response.ok) {
        toast.success('Categories guardades');
      }
    } catch (error) {
      console.error('Error saving categories:', error);
      toast.error('Error guardant categories');
    } finally {
      setSavingCategories(false);
    }
  };

  const deleteResponse = async (id) => {
    if (!confirm('Eliminar aquesta resposta?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/survey/admin/response/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Resposta eliminada');
        fetchResponses();
      }
    } catch (error) {
      console.error('Error deleting response:', error);
      toast.error('Error eliminant resposta');
    }
  };

  const exportCSV = () => {
    if (!report?.responses?.length) return;
    
    const headers = ['Data Visita', 'Mitjana', 'Nom', 'Email', 'Telèfon', 'Comentari', 'Data Enviament'];
    const rows = report.responses.map(r => [
      r.visit_date,
      r.average_rating,
      r.name || '',
      r.email || '',
      r.phone || '',
      (r.comment || '').replace(/"/g, '""'),
      new Date(r.submitted_at).toLocaleDateString('ca-ES')
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `enquestes_${filters.startDate || 'all'}_${filters.endDate || 'all'}.csv`;
    link.click();
  };

  const copyLink = (lang) => {
    const url = `${SITE_URL}/enquesta?lang=${lang}`;
    navigator.clipboard.writeText(url);
    toast.success('Enllaç copiat');
  };

  const addCategory = () => {
    setCategories([
      ...categories,
      {
        id: `cat_${Date.now()}`,
        name_ca: 'Nova categoria',
        name_es: 'Nueva categoría',
        name_fr: 'Nouvelle catégorie',
        name_en: 'New category',
        order: categories.length + 1,
        active: true
      }
    ]);
  };

  const updateCategory = (id, field, value) => {
    setCategories(categories.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const deleteCategory = (id) => {
    setCategories(categories.filter(c => c.id !== id));
  };

  const moveCategory = (index, direction) => {
    const newCategories = [...categories];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= categories.length) return;
    [newCategories[index], newCategories[newIndex]] = [newCategories[newIndex], newCategories[index]];
    newCategories.forEach((c, i) => c.order = i + 1);
    setCategories(newCategories);
  };

  const getRatingEmoji = (rating) => {
    const emojis = { 1: '😞', 2: '😕', 3: '😐', 4: '🙂', 5: '😊' };
    return emojis[rating] || '❓';
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600 bg-green-50';
    if (rating >= 3.5) return 'text-lime-600 bg-lime-50';
    if (rating >= 2.5) return 'text-yellow-600 bg-yellow-50';
    if (rating >= 1.5) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-survey">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E]">
              Enquestes de Satisfacció
            </h1>
            <p className="text-sm text-[#666666] mt-1">
              Gestiona i consulta les valoracions dels clients
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E5E5E5]">
          {[
            { id: 'responses', label: 'Respostes', icon: MessageSquare },
            { id: 'report', label: 'Informe', icon: BarChart3 },
            { id: 'categories', label: 'Categories', icon: Settings },
            { id: 'links', label: 'Enllaços', icon: ExternalLink }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#1A4D2E] text-[#1A4D2E]'
                  : 'border-transparent text-[#666666] hover:text-[#333333]'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date Filters (for responses and report) */}
        {(activeTab === 'responses' || activeTab === 'report') && (
          <div className="flex flex-wrap gap-4 items-end bg-[#F9F9F7] p-4 rounded-lg">
            <div>
              <label className="block text-xs text-[#666666] mb-1">Data inici</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs text-[#666666] mb-1">Data fi</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg"
              />
            </div>
            <Button
              onClick={() => setFilters({ startDate: '', endDate: '' })}
              variant="outline"
              size="sm"
            >
              Netejar filtres
            </Button>
            {activeTab === 'report' && report?.responses?.length > 0 && (
              <Button
                onClick={exportCSV}
                className="bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white ml-auto"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            )}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
          </div>
        ) : (
          <>
            {/* Responses Tab */}
            {activeTab === 'responses' && (
              <div className="space-y-4">
                <p className="text-sm text-[#666666]">
                  Total: <strong>{totalResponses}</strong> respostes
                </p>
                
                {responses.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-[#E5E5E5]">
                    <MessageSquare className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
                    <p className="text-[#666666]">Encara no hi ha respostes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {responses.map((resp) => (
                      <div 
                        key={resp.id}
                        className="bg-white rounded-lg border border-[#E5E5E5] overflow-hidden"
                      >
                        <div 
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#F9F9F7]"
                          onClick={() => setExpandedResponse(expandedResponse === resp.id ? null : resp.id)}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`px-3 py-1 rounded-full text-lg font-medium ${getRatingColor(resp.average_rating)}`}>
                              {resp.average_rating.toFixed(1)} {getRatingEmoji(Math.round(resp.average_rating))}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-[#888888]" />
                                <span className="text-sm text-[#666666]">
                                  Visita: {new Date(resp.visit_date).toLocaleDateString('ca-ES')}
                                </span>
                              </div>
                              {resp.name && (
                                <p className="text-sm font-medium text-[#333333]">{resp.name}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#888888]">
                              {new Date(resp.submitted_at).toLocaleDateString('ca-ES')}
                            </span>
                            {expandedResponse === resp.id ? (
                              <ChevronUp className="w-5 h-5 text-[#888888]" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-[#888888]" />
                            )}
                          </div>
                        </div>
                        
                        {expandedResponse === resp.id && (
                          <div className="border-t border-[#E5E5E5] p-4 bg-[#F9F9F7]">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                              {resp.ratings.map((r, i) => (
                                <div key={i} className="text-center">
                                  <p className="text-2xl">{getRatingEmoji(r.rating)}</p>
                                  <p className="text-xs text-[#888888] mt-1">{r.category_id.replace('cat_', '')}</p>
                                </div>
                              ))}
                            </div>
                            
                            {resp.comment && (
                              <div className="bg-white p-3 rounded-lg mb-4">
                                <p className="text-sm text-[#666666] italic">"{resp.comment}"</p>
                              </div>
                            )}
                            
                            <div className="flex flex-wrap gap-4 text-sm text-[#666666]">
                              {resp.email && <span>📧 {resp.email}</span>}
                              {resp.phone && <span>📞 {resp.phone}</span>}
                            </div>
                            
                            <div className="mt-4 pt-4 border-t border-[#E5E5E5] flex justify-end">
                              <button
                                onClick={() => deleteResponse(resp.id)}
                                className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1"
                              >
                                <Trash2 className="w-4 h-4" />
                                Eliminar
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Report Tab */}
            {activeTab === 'report' && report && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg border border-[#E5E5E5] p-6 text-center">
                    <p className="text-4xl font-bold text-[#1A4D2E]">{report.total_responses}</p>
                    <p className="text-sm text-[#666666] mt-1">Respostes totals</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#E5E5E5] p-6 text-center">
                    <p className={`text-4xl font-bold ${getRatingColor(report.average_rating).split(' ')[0]}`}>
                      {report.average_rating.toFixed(2)}
                    </p>
                    <p className="text-sm text-[#666666] mt-1">Mitjana global</p>
                  </div>
                  <div className="bg-white rounded-lg border border-[#E5E5E5] p-6 text-center">
                    <p className="text-4xl">{getRatingEmoji(Math.round(report.average_rating))}</p>
                    <p className="text-sm text-[#666666] mt-1">Satisfacció</p>
                  </div>
                </div>

                {/* Category Averages */}
                <div className="bg-white rounded-lg border border-[#E5E5E5] p-6">
                  <h3 className="font-semibold text-[#333333] mb-4">Mitjana per categoria</h3>
                  <div className="space-y-3">
                    {report.category_averages.map((cat) => (
                      <div key={cat.id} className="flex items-center gap-4">
                        <span className="w-48 text-sm text-[#666666]">{cat.name_ca}</span>
                        <div className="flex-1 bg-[#E5E5E5] rounded-full h-4 overflow-hidden">
                          <div 
                            className="h-full bg-[#1A4D2E] transition-all"
                            style={{ width: `${(cat.average / 5) * 100}%` }}
                          />
                        </div>
                        <span className={`px-2 py-0.5 rounded text-sm font-medium ${getRatingColor(cat.average)}`}>
                          {cat.average.toFixed(1)}
                        </span>
                        <span className="text-xs text-[#888888]">({cat.count})</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Responses List */}
                <div className="bg-white rounded-lg border border-[#E5E5E5] overflow-hidden">
                  <div className="p-4 border-b border-[#E5E5E5]">
                    <h3 className="font-semibold text-[#333333]">Detall de respostes</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F9F9F7]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#666666] uppercase">Data Visita</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#666666] uppercase">Mitjana</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#666666] uppercase">Nom</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#666666] uppercase">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#666666] uppercase">Telèfon</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E5E5]">
                        {report.responses.map((resp) => (
                          <tr key={resp.id} className="hover:bg-[#F9F9F7]">
                            <td className="px-4 py-3 text-sm">{new Date(resp.visit_date).toLocaleDateString('ca-ES')}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-sm ${getRatingColor(resp.average_rating)}`}>
                                {resp.average_rating.toFixed(1)} {getRatingEmoji(Math.round(resp.average_rating))}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm">{resp.name || '-'}</td>
                            <td className="px-4 py-3 text-sm">{resp.email || '-'}</td>
                            <td className="px-4 py-3 text-sm">{resp.phone || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-[#666666]">
                    Edita les categories de l'enquesta
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={addCategory} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Afegir categoria
                    </Button>
                    <Button 
                      onClick={saveCategories} 
                      disabled={savingCategories}
                      className="bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white"
                    >
                      {savingCategories ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Guardar
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  {categories.map((cat, index) => (
                    <div 
                      key={cat.id}
                      className="bg-white rounded-lg border border-[#E5E5E5] p-4"
                    >
                      <div className="flex items-center gap-4 mb-3">
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => moveCategory(index, -1)}
                            disabled={index === 0}
                            className="p-0.5 hover:bg-[#F5F5F5] rounded disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => moveCategory(index, 1)}
                            disabled={index === categories.length - 1}
                            className="p-0.5 hover:bg-[#F5F5F5] rounded disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="text-sm font-medium text-[#666666] w-6">{index + 1}</span>
                        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                          <input
                            type="text"
                            value={cat.name_ca}
                            onChange={(e) => updateCategory(cat.id, 'name_ca', e.target.value)}
                            placeholder="Català"
                            className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg"
                          />
                          <input
                            type="text"
                            value={cat.name_es}
                            onChange={(e) => updateCategory(cat.id, 'name_es', e.target.value)}
                            placeholder="Español"
                            className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg"
                          />
                          <input
                            type="text"
                            value={cat.name_fr}
                            onChange={(e) => updateCategory(cat.id, 'name_fr', e.target.value)}
                            placeholder="Français"
                            className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg"
                          />
                          <input
                            type="text"
                            value={cat.name_en}
                            onChange={(e) => updateCategory(cat.id, 'name_en', e.target.value)}
                            placeholder="English"
                            className="px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg"
                          />
                        </div>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={cat.active}
                            onChange={(e) => updateCategory(cat.id, 'active', e.target.checked)}
                            className="rounded"
                          />
                          <span className="text-xs text-[#666666]">Activa</span>
                        </label>
                        <button
                          onClick={() => deleteCategory(cat.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links Tab */}
            {activeTab === 'links' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-[#E5E5E5] p-6">
                  <h3 className="font-semibold text-[#333333] mb-4">
                    Enllaços a l'enquesta per idioma
                  </h3>
                  <p className="text-sm text-[#666666] mb-6">
                    Copia l'enllaç corresponent i envia'l als clients per email o WhatsApp
                  </p>
                  
                  <div className="space-y-3">
                    {[
                      { lang: 'ca', label: 'Català', flag: '🇦🇩' },
                      { lang: 'es', label: 'Español', flag: '🇪🇸' },
                      { lang: 'fr', label: 'Français', flag: '🇫🇷' },
                      { lang: 'en', label: 'English', flag: '🇬🇧' }
                    ].map(({ lang, label, flag }) => (
                      <div 
                        key={lang}
                        className="flex items-center justify-between p-4 bg-[#F9F9F7] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{flag}</span>
                          <div>
                            <p className="font-medium text-[#333333]">{label}</p>
                            <code className="text-xs text-[#888888]">
                              {SITE_URL}/enquesta?lang={lang}
                            </code>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => copyLink(lang)}
                            variant="outline"
                            size="sm"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copiar
                          </Button>
                          <a
                            href={`${SITE_URL}/enquesta?lang=${lang}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 text-sm border border-[#E5E5E5] rounded-lg hover:bg-white"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Obrir
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#1A4D2E]/5 rounded-lg p-6">
                  <h4 className="font-semibold text-[#1A4D2E] mb-2">
                    Com utilitzar
                  </h4>
                  <ul className="text-sm text-[#666666] space-y-2">
                    <li>1. Copia l'enllaç de l'idioma del client</li>
                    <li>2. Envia'l per email o WhatsApp amb un missatge personalitzat</li>
                    <li>3. El client omplirà l'enquesta i rebràs la resposta aquí</li>
                  </ul>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSurvey;
